import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  fetchAnnotatedEvidenceMediaBlob,
  getLatestAnnotatedEvidence,
  type AnnotatedEvidenceLatest,
} from '../../services/annotatedEvidenceApi';
import {
  liveViewPatternAreaLabel,
  liveViewPatternDisplayName,
  operatorFacingAreaLabel,
} from '../../domain/liveView/liveViewPatternDisplay';
import { looksLikeInternalSafetyId } from '../../domain/buyerFacing/conditionDisplayNames';
import {
  shouldBrowserOverlayAnnotatedEvidence,
  shouldIncludeOverlayHeader,
} from '../../domain/liveView/annotatedEvidenceOverlayPolicy';
import { formatEvidenceTimestampLocal } from '../../domain/liveView/formatEvidenceTimestamp';
import { overlayEvidenceSnapshotBlob } from '../../domain/liveView/overlayEvidenceSnapshot';
import { resolveOverlayZonesForCamera } from '../../domain/liveView/resolveOverlayZones';
import type { Zone } from '../../services/zonesApi';

const POLL_MS = 30000;

type PanelState = 'loading' | 'empty' | 'processing' | 'ready' | 'error';
type MediaView = 'snapshot' | 'clip';

function hasReadyMedia(payload: AnnotatedEvidenceLatest | null): boolean {
  if (!payload || payload.status !== 'ready') return false;
  if (payload.image_ready === true || payload.clip_ready === true) return true;
  return Boolean(payload.image_url || payload.clip_url);
}

function resolvePanelState(payload: AnnotatedEvidenceLatest | null, fetchFailed: boolean): PanelState {
  if (fetchFailed) return 'error';
  if (!payload) return 'loading';
  if (hasReadyMedia(payload)) return 'ready';
  if (payload.status === 'processing') return 'processing';
  if (payload.status === 'error') return 'error';
  return 'empty';
}

function imageSignature(data: AnnotatedEvidenceLatest): string {
  return [
    data.incident_id || '',
    data.image_url || '',
    data.image_ready ? '1' : '0',
  ].join('|');
}

function evidenceSignature(data: AnnotatedEvidenceLatest): string {
  return [
    imageSignature(data),
    data.clip_url || '',
    data.clip_ready ? '1' : '0',
  ].join('|');
}

function snapshotOverlayKey(data: AnnotatedEvidenceLatest, zoneCount: number): string {
  return `${imageSignature(data)}|zones:${zoneCount}`;
}

function formatClipWindow(startMs?: number, endMs?: number): string | null {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  try {
    const start = new Date(Number(startMs)).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
    const end = new Date(Number(endMs)).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
    return `${start} - ${end}`;
  } catch {
    return null;
  }
}

function preloadImageObjectUrl(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('snapshot preload failed'));
    img.src = url;
  });
}

export interface AnnotatedEvidencePanelProps {
  cameraId: string;
  edgeDeviceId?: string;
  cameraName?: string;
  active: boolean;
  showTechnicalIds?: boolean;
}

const AnnotatedEvidencePanel: React.FC<AnnotatedEvidencePanelProps> = ({
  cameraId,
  edgeDeviceId,
  cameraName,
  active,
  showTechnicalIds = false,
}) => {
  const [payload, setPayload] = useState<AnnotatedEvidenceLatest | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [clipSrc, setClipSrc] = useState<string | null>(null);
  const [clipLoadFailed, setClipLoadFailed] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [clipLoading, setClipLoading] = useState(false);
  const [mediaView, setMediaView] = useState<MediaView>('snapshot');
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [zones, setZones] = useState<Zone[]>([]);
  const mountedRef = useRef(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const imageObjectUrlRef = useRef<string | null>(null);
  const clipObjectUrlRef = useRef<string | null>(null);
  const evidenceKeyRef = useRef('');
  const imageKeyRef = useRef('');
  const snapshotOverlayKeyRef = useRef('');
  const pollTimerRef = useRef<number | null>(null);

  const swapImageSrc = useCallback((nextUrl: string) => {
    const previous = imageObjectUrlRef.current;
    imageObjectUrlRef.current = nextUrl;
    setImageSrc(nextUrl);
    if (previous && previous !== nextUrl) {
      URL.revokeObjectURL(previous);
    }
  }, []);

  const swapClipSrc = useCallback((nextUrl: string) => {
    const previous = clipObjectUrlRef.current;
    clipObjectUrlRef.current = nextUrl;
    setClipSrc(nextUrl);
    if (previous && previous !== nextUrl) {
      URL.revokeObjectURL(previous);
    }
  }, []);

  const revokeMediaUrl = useCallback((kind: 'image' | 'clip') => {
    const ref = kind === 'image' ? imageObjectUrlRef : clipObjectUrlRef;
    if (ref.current) {
      URL.revokeObjectURL(ref.current);
      ref.current = null;
    }
    if (kind === 'image') setImageSrc(null);
    if (kind === 'clip') setClipSrc(null);
  }, []);

  const stopPolling = useCallback(() => {
    setPollingEnabled(false);
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    setPollingEnabled(true);
  }, []);

  const loadOverlayZones = useCallback(
    async (zoneId?: string) => {
      const resolved = await resolveOverlayZonesForCamera(cameraId, edgeDeviceId, zoneId);
      const list = Array.isArray(resolved) ? resolved : [];
      if (mountedRef.current) setZones(list);
      return list;
    },
    [cameraId, edgeDeviceId],
  );

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    void loadOverlayZones().then((resolved) => {
      if (!cancelled && resolved.length === 0 && payload?.zone) {
        void loadOverlayZones(payload.zone);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [active, cameraId, edgeDeviceId, loadOverlayZones, payload?.zone]);

  const loadSnapshot = useCallback(
    async (data: AnnotatedEvidenceLatest, overlayZones: Zone[]): Promise<string | null> => {
      if (!data.image_url) return null;
      try {
        const blob = await fetchAnnotatedEvidenceMediaBlob(data.image_url);
        const rendered = shouldBrowserOverlayAnnotatedEvidence(data)
          ? await overlayEvidenceSnapshotBlob(blob, overlayZones, {
              label: liveViewPatternDisplayName(data.event_type, data.event_type),
              ts: String(data.timestamp || ''),
              zone_id: data.zone,
              camera_id: cameraId,
              includeHeader: shouldIncludeOverlayHeader(data),
            })
          : blob;
        const typed = rendered.type ? rendered : new Blob([rendered], { type: 'image/jpeg' });
        return URL.createObjectURL(typed);
      } catch {
        return null;
      }
    },
    [cameraId],
  );

  const loadClip = useCallback(async (data: AnnotatedEvidenceLatest): Promise<string | null> => {
    if (!data.clip_url) return null;
    try {
      const blob = await fetchAnnotatedEvidenceMediaBlob(data.clip_url);
      const typed = blob.type ? blob : new Blob([blob], { type: 'video/mp4' });
      return URL.createObjectURL(typed);
    } catch {
      return null;
    }
  }, []);

  const applySnapshot = useCallback(
    async (data: AnnotatedEvidenceLatest, opts?: { silent?: boolean; overlayZones?: Zone[] }) => {
      if (!data.image_url) return;
      const overlayZones = opts?.overlayZones?.length
        ? opts.overlayZones
        : zones.length
          ? zones
          : await loadOverlayZones(data.zone);
      const hasExistingImage = Boolean(imageObjectUrlRef.current);
      if (!hasExistingImage && !opts?.silent) setSnapshotLoading(true);
      if (hasExistingImage) setSnapshotLoading(true);
      const imageObjectUrl = await loadSnapshot(data, overlayZones);
      if (!mountedRef.current) {
        if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
        return;
      }
      if (imageObjectUrl) {
        if (hasExistingImage) {
          try {
            await preloadImageObjectUrl(imageObjectUrl);
          } catch {
            URL.revokeObjectURL(imageObjectUrl);
            setSnapshotLoading(false);
            return;
          }
        }
        if (!mountedRef.current) {
          URL.revokeObjectURL(imageObjectUrl);
          return;
        }
        swapImageSrc(imageObjectUrl);
      }
      setSnapshotLoading(false);
    },
    [loadOverlayZones, loadSnapshot, swapImageSrc, zones],
  );

  const refresh = useCallback(async () => {
    if (!active || !pollingEnabled) return;
    try {
      const data = await getLatestAnnotatedEvidence(cameraId, edgeDeviceId);
      if (!mountedRef.current || !pollingEnabled) return;
      setFetchFailed(false);

      const nextKey = evidenceSignature(data);
      setPayload(data);

      if (nextKey === evidenceKeyRef.current) return;

      const nextImageKey = imageSignature(data);
      const imageChanged = nextImageKey !== imageKeyRef.current;
      evidenceKeyRef.current = nextKey;

      if (!imageChanged) return;

      imageKeyRef.current = nextImageKey;
      snapshotOverlayKeyRef.current = '';
      setClipLoadFailed(false);
      setMediaView('snapshot');
      revokeMediaUrl('clip');

      const overlayZones = await loadOverlayZones(data.zone);
      if (data.image_ready && data.image_url) {
        await applySnapshot(data, {
          silent: Boolean(imageObjectUrlRef.current),
          overlayZones,
        });
        snapshotOverlayKeyRef.current = snapshotOverlayKey(data, overlayZones.length);
      }
    } catch {
      if (!mountedRef.current) return;
      setFetchFailed(true);
    }
  }, [active, applySnapshot, cameraId, edgeDeviceId, loadOverlayZones, pollingEnabled, revokeMediaUrl]);

  useEffect(() => {
    if (!active || mediaView !== 'snapshot') return;
    if (!payload?.image_url || !payload.image_ready || zones.length === 0) return;

    const overlayKey = snapshotOverlayKey(payload, zones.length);
    if (overlayKey === snapshotOverlayKeyRef.current) return;

    void (async () => {
      await applySnapshot(payload, { silent: true, overlayZones: zones });
      snapshotOverlayKeyRef.current = overlayKey;
    })();
  }, [active, applySnapshot, mediaView, payload, zones]);

  const handlePlayClip = useCallback(async () => {
    if (!payload?.clip_ready || !payload.clip_url) return;
    stopPolling();
    setClipLoadFailed(false);
    setMediaView('clip');
    setClipLoading(true);
    revokeMediaUrl('clip');

    const clipObjectUrl = await loadClip(payload);
    if (!mountedRef.current) {
      if (clipObjectUrl) URL.revokeObjectURL(clipObjectUrl);
      return;
    }

    if (clipObjectUrl) {
      swapClipSrc(clipObjectUrl);
    } else {
      setClipLoadFailed(true);
      setMediaView('snapshot');
      startPolling();
    }
    setClipLoading(false);
  }, [loadClip, payload, revokeMediaUrl, startPolling, stopPolling, swapClipSrc]);

  useEffect(() => {
    if (!clipSrc || mediaView !== 'clip') return;
    const video = videoRef.current;
    if (!video) return;
    void video.play().catch(() => {
      // Browser may block autoplay until user gesture; controls still work.
    });
  }, [clipSrc, mediaView]);

  const handleBackToSnapshot = useCallback(() => {
    setMediaView('snapshot');
    startPolling();
  }, [startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      revokeMediaUrl('image');
      revokeMediaUrl('clip');
    };
  }, [revokeMediaUrl]);

  useEffect(() => {
    if (!active || !pollingEnabled) {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }

    void refresh();
    pollTimerRef.current = window.setInterval(() => {
      void refresh();
    }, POLL_MS);

    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [active, pollingEnabled, refresh]);

  const state = resolvePanelState(payload, fetchFailed);
  const eventLabel = liveViewPatternDisplayName(payload?.event_type, payload?.event_type);
  const zoneLabel = operatorFacingAreaLabel(payload?.zone, payload?.zone, payload?.zone)
    || (showTechnicalIds ? (liveViewPatternAreaLabel(payload?.zone, payload?.zone) || payload?.zone || null) : null);
  const timestampLabel = formatEvidenceTimestampLocal(payload?.timestamp);
  const showClip = mediaView === 'clip';
  const showImage = mediaView === 'snapshot';
  const showFirstLoadSpinner = state === 'ready' && snapshotLoading && !imageSrc && !showClip;
  const showEvidenceChrome = state === 'ready' && Boolean(payload) && (Boolean(imageSrc) || showClip || showFirstLoadSpinner);
  const clipWindowLabel = formatClipWindow(payload?.clip_start_ts_ms, payload?.clip_end_ts_ms);
  const clipIsContextOnly = payload?.clip_event_covered === false || String(payload?.clip_timing_basis || '').trim() === 'post_trigger_live_rtsp_tail';
  const incidentSubject = String(payload?.incident_subject_label || '').trim();
  const showIncidentSubject = Boolean(incidentSubject)
    && (showTechnicalIds || !looksLikeInternalSafetyId(incidentSubject));

  const evidenceHeader = payload ? (
    <Box sx={{ px: 1.25, py: 1, bgcolor: 'rgba(0,0,0,0.82)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 800, lineHeight: 1.3 }}
        data-testid="annotated-evidence-condition-name"
      >
        {eventLabel}
      </Typography>
    </Box>
  ) : null;

  const evidenceFooter = payload ? (
    <Box sx={{ p: 1.25, bgcolor: 'rgba(0,0,0,0.82)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      {showIncidentSubject ? (
        <Typography variant="caption" color="grey.400" display="block">
          {incidentSubject}
        </Typography>
      ) : null}
      {zoneLabel ? (
        <Typography variant="caption" color="grey.400" display="block">
          Area: {zoneLabel}
        </Typography>
      ) : null}
      {timestampLabel ? (
        <Typography variant="caption" color="grey.500" display="block">
          {timestampLabel}
        </Typography>
      ) : null}
      {showClip && clipWindowLabel ? (
        <Typography variant="caption" color="grey.500" display="block">
          Clip window: {clipWindowLabel}
        </Typography>
      ) : null}
      {showTechnicalIds && typeof payload.confidence === 'number' && payload.confidence > 0 ? (
        <Typography variant="caption" color="grey.500" display="block">
          Confidence: {Math.round(payload.confidence * 100)}%
        </Typography>
      ) : null}
      {showTechnicalIds && showClip && payload.clip_timing_basis ? (
        <Typography variant="caption" color="grey.500" display="block">
          Clip timing basis: {String(payload.clip_timing_basis).replace(/[_-]+/g, ' ')}
        </Typography>
      ) : null}
      {showTechnicalIds ? (
        <Box data-testid="annotated-evidence-advanced-ids" sx={{ mt: 0.5 }}>
          {payload.incident_id ? (
            <Typography variant="caption" color="grey.500" display="block">
              Incident: {payload.incident_id}
            </Typography>
          ) : null}
          {cameraId ? (
            <Typography variant="caption" color="grey.500" display="block">
              Camera: {cameraId}
            </Typography>
          ) : null}
          {edgeDeviceId ? (
            <Typography variant="caption" color="grey.500" display="block">
              Device: {edgeDeviceId}
            </Typography>
          ) : null}
          {payload.zone ? (
            <Typography variant="caption" color="grey.500" display="block">
              Zone: {payload.zone}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {showClip && clipIsContextOnly ? (
        <Alert severity="warning" sx={{ mt: 1, mb: 0.5 }}>
          This clip is later context, not trusted event-centered proof.
        </Alert>
      ) : null}
      {showImage && payload.trigger_person_verification_label ? (
        <Alert
          severity={String(payload.trigger_person_verification_status || '').trim().toLowerCase() === 'verified' ? 'success' : 'info'}
          sx={{ mt: 1, mb: 0.5 }}
        >
          {payload.trigger_person_verification_label}
        </Alert>
      ) : null}
      {showImage && payload.clip_ready ? (
        <Button
          size="small"
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
          disabled={clipLoading}
          onClick={() => {
            void handlePlayClip();
          }}
        >
          {clipLoading ? 'Loading clip…' : clipIsContextOnly ? 'Play context clip' : 'Play raw event clip'}
        </Button>
      ) : null}
      {showClip ? (
        <Button
          size="small"
          variant="text"
          color="inherit"
          sx={{ mt: 0.5 }}
          onClick={handleBackToSnapshot}
        >
          Back to annotated snapshot
        </Button>
      ) : null}
      {payload.image_ready && !payload.clip_ready ? (
        <Typography variant="caption" color="grey.500" display="block" sx={{ mt: 0.5 }}>
          Event clip may still be processing.
        </Typography>
      ) : null}
      {clipLoadFailed ? (
        <Typography variant="caption" color="grey.500" display="block" sx={{ mt: 0.5 }}>
          Event clip could not be loaded.
        </Typography>
      ) : null}
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0b0b0b',
        color: 'common.white',
        zIndex: 1,
      }}
      data-testid="annotated-evidence-panel"
    >
      {state === 'loading' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={36} sx={{ color: 'grey.400', mb: 1.5 }} />
          <Typography variant="body2" color="grey.300">
            Loading annotated evidence…
          </Typography>
        </Box>
      )}

      {state === 'empty' && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Alert severity="info" sx={{ width: '100%', maxWidth: 420 }}>
            {payload?.message || 'No annotated evidence yet. Safety events will appear here when detected.'}
          </Alert>
        </Box>
      )}

      {state === 'processing' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={32} sx={{ color: 'grey.400', mb: 1.5 }} />
          <Typography variant="body2" color="grey.300">
            {payload?.message || 'Processing latest safety evidence…'}
          </Typography>
        </Box>
      )}

      {state === 'error' && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Alert severity="warning" sx={{ width: '100%', maxWidth: 420 }}>
            Annotated evidence is temporarily unavailable. Live View remains available.
          </Alert>
        </Box>
      )}

      {state === 'ready' && showFirstLoadSpinner && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={32} sx={{ color: 'grey.400', mb: 1.5 }} />
          <Typography variant="body2" color="grey.300">
            Loading evidence media…
          </Typography>
        </Box>
      )}

      {state === 'ready' && !showFirstLoadSpinner && showImage && !imageSrc && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Alert severity="warning" sx={{ width: '100%', maxWidth: 420 }}>
            Evidence metadata is available but snapshot could not be loaded.
          </Alert>
        </Box>
      )}

      {state === 'ready' && showEvidenceChrome && !showFirstLoadSpinner && (showImage || showClip) && (
        <>
          {evidenceHeader}
          <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
            {showImage && imageSrc ? (
              <>
                <img
                  src={imageSrc}
                  alt={`Annotated evidence for ${cameraName || (showTechnicalIds ? cameraId : 'camera')}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#000',
                  }}
                />
                {snapshotLoading ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(0,0,0,0.55)',
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: 'grey.300' }} />
                    <Typography variant="caption" color="grey.300">
                      Updating…
                    </Typography>
                  </Box>
                ) : null}
              </>
            ) : null}
            {showClip ? (
              <>
                <video
                  ref={videoRef}
                  key={clipSrc || 'clip-pending'}
                  src={clipSrc || undefined}
                  controls
                  playsInline
                  onError={() => {
                    setClipLoadFailed(true);
                    revokeMediaUrl('clip');
                    setMediaView('snapshot');
                    startPolling();
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#000',
                  }}
                />
                {clipLoading ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.45)',
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: 'grey.300' }} />
                  </Box>
                ) : null}
              </>
            ) : null}
          </Box>
          {evidenceFooter}
        </>
      )}

      {state === 'ready' && payload && !showEvidenceChrome && !showFirstLoadSpinner && (
        <>
          {evidenceHeader}
          {evidenceFooter}
        </>
      )}
    </Box>
  );
};

export default AnnotatedEvidencePanel;
